'use client'

import * as yup from 'yup'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useCallback, useState } from 'react'
import { useTranslations } from 'next-intl'

import { useRouter } from '@/lib/i18n'
import { FormInput } from '@/components/Base/Form'
import { emailRegex } from '@/utils'
import { requestPasswordReset } from '@/app/_actions'
import Button from '@/components/Base/Button'
import Lucide from '../Base/Lucide'
import LocaleSwitcher from '../Locale/LocaleSwitcher'
import { getCurrentYear } from '@/utils/helper'

export default function ResetPasswordForm() {
    const router = useRouter()
    const t = useTranslations('ResetPassword')
    const tCommon = useTranslations('Common')

    const [isLoading, setIsLoading] = useState(false)
    const [success, setSuccess] = useState(false)

    const formValidationSchema = () => {
        return yup.object().shape({
            email: yup
                .string()
                .matches(emailRegex, {
                    message: t('Error.invalid_email'),
                    excludeEmptyString: true,
                })
                .required(t('Error.required_email')),
        })
    }

    const {
        register,
        handleSubmit,
        getValues,
        setError,
        formState: { errors },
    } = useForm({ resolver: yupResolver(formValidationSchema()) })

    const onFormSubmit = useCallback(async () => {
        const values = getValues()
        // add or update values before calling parent submit handler

        setIsLoading(true)
        requestPasswordReset(values.email).then((result) => {
            if ('error' in result) {
                setError('root.serverError', {
                    type: 'serverError',
                    message: t('Error.reset_fail'),
                })
                setIsLoading(false)
            } else {
                setSuccess(true)
            }
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getValues, router, setError, t])

    return (
        <div className='flex h-full flex-col'>
            <div className='flex justify-end'>
                <LocaleSwitcher />
            </div>
            <form
                method='post'
                onSubmit={handleSubmit(onFormSubmit)}
                className='mx-auto my-auto w-full rounded-md bg-white px-5 py-8 shadow-md dark:bg-darkmode-600 sm:w-3/4 sm:px-8 lg:w-2/4 xl:ml-20 xl:w-auto xl:bg-transparent xl:p-0 xl:shadow-none'
            >
                <h2 className='intro-x flex items-center text-center text-2xl font-bold xl:relative xl:text-left xl:text-3xl'>
                    <Lucide
                        icon='ArrowLeft'
                        className='-left-8 mr-1 h-6 w-6 cursor-pointer xl:absolute xl:mr-0'
                        onClick={() => router.push('/login')}
                    />
                    {t('title')}
                </h2>
                {!success ? (
                    <>
                        <div className='intro-x mt-8'>
                            <div>
                                <FormInput
                                    type='text'
                                    className='intro-x block min-w-full px-4 py-3 xl:min-w-[350px]'
                                    placeholder={t('email')}
                                    {...register('email')}
                                />
                                {errors.email && (
                                    <div className='mt-2 text-danger'>
                                        {errors.email.message}
                                    </div>
                                )}
                            </div>
                            {errors.root?.serverError && (
                                <div className='mt-2 text-danger'>
                                    {errors.root?.serverError.message}
                                </div>
                            )}
                        </div>
                        <div className='intro-x mt-5 text-center xl:mt-8 xl:text-left'>
                            <Button
                                variant='primary'
                                className='w-full px-4 py-3 align-top xl:mr-3 xl:w-32'
                                type='submit'
                                disabled={isLoading}
                            >
                                {t('submit')}
                            </Button>
                        </div>
                    </>
                ) : (
                    <div className='mt-8 text-success'>{t('success')}</div>
                )}

                <div className='intro-x mt-10 text-center text-slate-600 dark:text-slate-500 xl:mt-24 xl:text-left'>
                    {tCommon('copyright', { year: getCurrentYear() })}
                </div>
            </form>
        </div>
    )
}
